import DetailsPage from '@/components/details-page';
import { redirectToErrorPage } from '@/components/error-detail/ErrorDetailPage.component';
import LinearLoading from '@/components/linear-loading';
import SimpleTabs, { SimpleTabsProps, TabData } from '@/components/simple-tabs';
import { useUserToken } from '@/contexts/UserTokenContext';
import { useHeap } from '@/hooks';
import { useFixPlanRoute } from '@/hooks/useFixPlanRoute.hook';
import { ContributionDetailsDto, PlanV2Dto } from '@/models';
import { FeatureLevelPermissions } from '@/models/UserPermissions.model';
import ContributionService from '@/services/Contribution.service';
import { PlanService } from '@/services/Plan.service';
import { userService } from '@/services/User.service';
import Grid from '@mui/material/Unstable_Grid2';
import { useMutation, useQuery } from '@tanstack/react-query';

import { AxiosError } from 'axios';
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { usePlanType } from '../plan-detail/usePlanType.hook';
import { Actions } from './Actions';
import { DetailsTab } from './DetailsTab';
import PlanContributionOverview from './PlanContributionOverview';

type PlanContributionDetailRouteParams = {
  sponsorPlanId: string;
  ucid: string;
};

// moshe: rename to ContributionDetailRoute
const PlanContributionDetailRoute: React.FunctionComponent = () => {
  const { userHasValidToken } = useUserToken();
  const location = useLocation();
  const navigate = useNavigate();

  const { sponsorPlanId, ucid } =
    useParams<PlanContributionDetailRouteParams>();

  const sponsorPlanQuery = useQuery<PlanV2Dto>(
    ['PlanService.getPlanById', sponsorPlanId],
    () => PlanService.getPlanById(sponsorPlanId),
    {
      enabled: userHasValidToken && Boolean(sponsorPlanId),
      staleTime: Infinity
    }
  );

  const planType = usePlanType({
    recordkeeper:
      '' + sponsorPlanQuery?.data?.data?.relationships?.recordKeeper?.data?.id,
    type: sponsorPlanQuery?.data?.data?.attributes?.type
  });

  useFixPlanRoute();

  const paths = [
    {
      name: planType.urlPlansTypeName,
      to: `/${planType.urlPlansType}`
    },
    {
      name: sponsorPlanQuery?.data?.data?.attributes?.name ?? '--',
      to: `/${planType.urlPlansType}/${sponsorPlanId}`
    }
  ];

  const contributionQuery = useQuery<
    ContributionDetailsDto,
    AxiosError,
    ContributionDetailsDto,
    string[]
  >(
    [ContributionService.getContributionDetails.name, sponsorPlanId, ucid],
    async () => {
      return ContributionService.getContributionDetails({
        includeCorrections: true,
        planId: sponsorPlanId,
        sponsorId:
          sponsorPlanQuery?.data?.data?.relationships?.sponsor?.data?.id,
        ucid
      });
    },
    {
      enabled:
        userHasValidToken &&
        Boolean(sponsorPlanId && ucid && sponsorPlanQuery?.data?.data),
      refetchOnMount: 'always'
    }
  );

  const user = useMemo(() => userService.getUser(), []);

  const isOverviewPage = useMemo(
    () => location?.pathname?.includes('/overview'),
    [location]
  );

  useHeap(user);

  const cantAccessEsa506472 =
    !userService.hasPermission(FeatureLevelPermissions.READ_ESA_506472) &&
    +sponsorPlanId === 506472;

  const hasPlan1ReadPermission = userService.hasPermission(
    FeatureLevelPermissions.READ_PLAN_1
  );

  const hasJHPlansReadPermission = userService.hasPermission(
    FeatureLevelPermissions.READ_HANCOCK_PLANS
  );

  const params = useParams();

  const postResume = useMutation(
    (body: { ucid: string; sponsorId: number; sponsorPlanId: number }) =>
      ContributionService.postResume(body),
    {
      onSuccess: (data, variables) => {
        const pages = {
          CONFIRMATION_PAGE: `/plans/${variables.sponsorPlanId}/contributions/${variables.ucid}/submission/confirmation`,
          OVERVIEW_PAGE: `/plans/${variables.sponsorPlanId}/contributions/${variables.ucid}/overview`,
          SUBMISSION_PAGE: `/plans/${variables.sponsorPlanId}/contributions/${variables.ucid}/submission`
        };

        navigate(
          `${pages[data.redirectTo] ?? ''}${data.isEdit ? '?isEdit=true' : ''}`
        );
      }
    }
  );

  const plan = useQuery(
    [PlanService.getPlanById.name, params.sponsorPlanId],
    () => PlanService.getPlanById(params.sponsorPlanId),
    {
      enabled: !!params.sponsorPlanId,
      staleTime: Infinity,
      suspense: true
    }
  );

  useEffect(() => {
    if (
      userService.hasPermission(FeatureLevelPermissions.WRITE_CONTRIBUTION) &&
      plan.data?.data.relationships.sponsor.data.id
    ) {
      postResume.mutateAsync({
        sponsorId: plan.data?.data.relationships.sponsor.data.id,
        sponsorPlanId: +sponsorPlanId,
        ucid
      });
    }
  }, [plan.data]);

  useEffect(() => {
    if (
      params.planType !== planType.urlPlansType &&
      sponsorPlanQuery?.data?.data
    ) {
      const locationPathArray = location.pathname.split('/');
      locationPathArray[1] = planType.urlPlansType;
      return navigate(locationPathArray.join('/'), { replace: true });
    }
  }, [params, location, planType, sponsorPlanQuery?.data?.data]);

  if (
    (sponsorPlanId === '1' && !hasPlan1ReadPermission) ||
    cantAccessEsa506472 ||
    (sponsorPlanQuery?.data?.data?.relationships?.commercialPlatform?.data
      ?.id === 3 &&
      !hasJHPlansReadPermission)
  ) {
    return redirectToErrorPage(new Error('403'));
  }

  if (sponsorPlanQuery.isFetching || contributionQuery.isFetching) {
    // do we use this class for test assertions?
    return <LinearLoading />;
  }

  if (!sponsorPlanQuery.isSuccess || !contributionQuery.isSuccess) {
    return redirectToErrorPage(
      new Error(
        (
          sponsorPlanQuery?.error || contributionQuery?.error?.response?.status
        )?.toString()
      )
    );
  }

  // should isStateIRA be extracted to a react Provider Context?
  const isStateIRA =
    sponsorPlanQuery?.data?.data?.attributes?.type === 'State IRA' ||
    sponsorPlanQuery?.data?.data?.relationships?.recordKeeper?.data?.id === 76;

  const error = sponsorPlanQuery.error || contributionQuery.error;
  if (error) {
    return redirectToErrorPage(error as Error);
  }

  const makeTabsElement = (): SimpleTabsProps => {
    const PlanContributionTab: TabData = {
      component: (
        <PlanContributionOverview
          contributionData={contributionQuery.data}
          isStateIRA={isStateIRA}
          planQuery={sponsorPlanQuery}
          sponsorPlanId={sponsorPlanId}
          ucid={ucid}
        />
      ),
      label: 'Overview',
      path: `/plans/${sponsorPlanId}/contributions/${ucid}/overview`
    };

    const DetailTab: TabData = {
      component: (
        <DetailsTab
          planId={+sponsorPlanId}
          sponsorId={
            sponsorPlanQuery?.data?.data?.relationships?.sponsor?.data?.id
          }
          ucid={ucid}
        />
      ),
      label: 'Details',
      path: `/plans/${sponsorPlanId}/contributions/${ucid}/details`
    };

    const registeredTabs = [PlanContributionTab, DetailTab];

    const hideDetailsTab = (tab: TabData) =>
      (['Vestwell Sub-Accounting Platform', 'Vestwell ESA'].includes(
        contributionQuery.data?.recordkeeper
      ) &&
        tab.label === 'Details') ||
      tab.label !== 'Details';

    const filteredTabs = registeredTabs.filter(hideDetailsTab);

    const displayTabs: SimpleTabsProps = {
      tabs: filteredTabs,
      tabsAriaLabel: 'plan-detail-tabs'
    };

    return displayTabs;
  };

  const tabs = makeTabsElement();

  return (
    <Grid container>
      <Grid>
        <Grid>
          <DetailsPage
            copyableNumber={contributionQuery.data?.ucid}
            flowSubtype={contributionQuery.data?.key?.flowSubtype}
            isStateIRA={isStateIRA}
            paths={paths}
            title={sponsorPlanQuery?.data?.data?.attributes?.name ?? '--'}
            variant='contribution'
          />
        </Grid>
        {isOverviewPage && <Actions planId={+sponsorPlanId} ucid={ucid} />}
      </Grid>
      <SimpleTabs {...tabs} />
    </Grid>
  );
};

export default PlanContributionDetailRoute;
